import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Heart, Clock, ChevronLeft, ChevronRight, RefreshCw, Tv, Info, X, GripVertical } from 'lucide-react';
import { apiCall } from '../../services/api';

// Time slot configuration - responsive values
const SLOT_WIDTH = 180; // pixels per 30 minutes (desktop)
const SLOT_WIDTH_MOBILE = 120; // pixels per 30 minutes (mobile)
const HOUR_WIDTH = SLOT_WIDTH * 2;
const HOUR_WIDTH_MOBILE = SLOT_WIDTH_MOBILE * 2;
const CHANNEL_ROW_HEIGHT = 80;
const CHANNEL_ROW_HEIGHT_MOBILE = 70;
const TIME_HEADER_HEIGHT = 50;
const TIME_HEADER_HEIGHT_MOBILE = 40;
const CHANNEL_SIDEBAR_WIDTH = 200;
const CHANNEL_SIDEBAR_WIDTH_MOBILE = 140;

// CST timezone offset in milliseconds (UTC-6)
const CST_OFFSET = -6 * 60 * 60 * 1000;

// Helper to get current time in CST
const getCSTTime = () => {
  const now = new Date();
  // Get UTC time and add CST offset
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
  return utcTime + CST_OFFSET;
};

// Helper to format time in CST
const formatTimeCST = (timestamp) => {
  const date = new Date(timestamp);
  // Convert to CST
  const utcTime = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
  const cstTime = new Date(utcTime + CST_OFFSET);
  return cstTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'UTC' });
};

// Helper to parse date - handles both string and timestamp
const parseDate = (dateStr, timestamp) => {
  // Prefer timestamp if available (in seconds, convert to ms)
  if (timestamp) {
    return new Date(parseInt(timestamp) * 1000);
  }
  // Parse string as UTC by appending Z
  if (dateStr && !dateStr.endsWith('Z')) {
    return new Date(dateStr.replace(' ', 'T') + 'Z');
  }
  return new Date(dateStr);
};

// Helper to format time in CST timezone
const formatTime = (dateStr, timestamp) => {
  if (!dateStr && !timestamp) return '';
  const date = parseDate(dateStr, timestamp);
  // Format in CST timezone (America/Chicago)
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true,
    timeZone: 'America/Chicago'
  });
};

// Helper to check if program is currently airing
const isCurrentlyAiring = (program) => {
  const now = Date.now();
  const startTime = parseDate(program.start, program.start_timestamp).getTime();
  const endTime = parseDate(program.end, program.stop_timestamp).getTime();
  return now >= startTime && now < endTime;
};

// Helper to calculate program width in pixels (clipped to timeline bounds)
const calculateProgramWidth = (program, timelineStart, timelineEnd) => {
  const programStart = parseDate(program.start, program.start_timestamp).getTime();
  const programEnd = parseDate(program.end, program.stop_timestamp).getTime();
  
  // Clip start time to timeline start (for programs that started before the visible window)
  const effectiveStart = Math.max(programStart, timelineStart);
  // Clip end time to timeline end
  const effectiveEnd = timelineEnd ? Math.min(programEnd, timelineEnd) : programEnd;
  
  const durationMinutes = (effectiveEnd - effectiveStart) / (1000 * 60);
  return Math.max(0, (durationMinutes / 30) * SLOT_WIDTH);
};

// Helper to calculate program left offset (programs starting before timeline start at 0)
const calculateProgramLeft = (program, timelineStart) => {
  const startTime = parseDate(program.start, program.start_timestamp).getTime();
  // If program started before timeline, position at 0 (left edge)
  if (startTime <= timelineStart) {
    return 0;
  }
  const offsetMinutes = (startTime - timelineStart) / (1000 * 60);
  return (offsetMinutes / 30) * SLOT_WIDTH;
};

// Time header component - displays times in CST
const TimeHeader = ({ timelineStart, hoursToShow, isMobile }) => {
  const timeSlots = [];
  const slotWidth = isMobile ? SLOT_WIDTH_MOBILE : SLOT_WIDTH;
  const headerHeight = isMobile ? TIME_HEADER_HEIGHT_MOBILE : TIME_HEADER_HEIGHT;
  const sidebarWidth = isMobile ? CHANNEL_SIDEBAR_WIDTH_MOBILE : CHANNEL_SIDEBAR_WIDTH;
  
  for (let i = 0; i < hoursToShow * 2; i++) {
    const slotTime = timelineStart + (i * 30 * 60 * 1000);
    // Format the slot time in CST timezone
    const slotDate = new Date(slotTime);
    const label = slotDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true,
      timeZone: 'America/Chicago'
    });
    timeSlots.push({
      time: slotTime,
      label
    });
  }
  
  return (
    <div 
      className="flex bg-black border-b border-white/10 sticky top-0 z-20"
      style={{ height: headerHeight, marginLeft: sidebarWidth }}
    >
      {timeSlots.map((slot, idx) => (
        <div
          key={idx}
          className={`flex-shrink-0 border-l border-white/10 px-2 sm:px-3 py-1 sm:py-2 text-gray-400 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}
          style={{ width: slotWidth }}
        >
          {slot.label}
        </div>
      ))}
    </div>
  );
};

// Now playing indicator line
const NowIndicator = ({ timelineStart, currentTime, isMobile }) => {
  const slotWidth = isMobile ? SLOT_WIDTH_MOBILE : SLOT_WIDTH;
  const sidebarWidth = isMobile ? CHANNEL_SIDEBAR_WIDTH_MOBILE : CHANNEL_SIDEBAR_WIDTH;
  
  // Calculate left position based on current time
  const offsetMinutes = (currentTime - timelineStart) / (1000 * 60);
  const left = sidebarWidth + (offsetMinutes / 30) * slotWidth;
  
  // Don't show if current time is outside visible range
  if (offsetMinutes < 0 || offsetMinutes > 4 * 60) return null;
  
  return (
    <div 
      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30 pointer-events-none"
      style={{ left }}
    >
      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-500 rounded-full" />
    </div>
  );
};

// Program block component
const ProgramBlock = ({ program, timelineStart, timelineEnd, onSelect, isPlaying }) => {
  // Skip programs that end before the timeline starts
  const endTime = parseDate(program.end, program.stop_timestamp).getTime();
  if (endTime <= timelineStart) return null;
  
  // Skip programs that start after timeline ends
  const startTime = parseDate(program.start, program.start_timestamp).getTime();
  if (timelineEnd && startTime >= timelineEnd) return null;
  
  const width = calculateProgramWidth(program, timelineStart, timelineEnd);
  const left = calculateProgramLeft(program, timelineStart);
  const isLive = isCurrentlyAiring(program);
  
  if (width <= 0) return null;
  
  // Use 2px gap between cards (1px margin on each side)
  const cardMargin = 2;
  const displayLeft = left + cardMargin;
  const displayWidth = Math.max(width - (cardMargin * 2), 36);
  
  return (
    <div
      className={`absolute top-1 bottom-1 rounded-lg px-2 py-1 overflow-hidden cursor-pointer transition-all duration-200 group
        ${isLive 
          ? 'bg-cyan-600/30 border border-cyan-500/50 hover:bg-cyan-600/50 hover:border-cyan-400' 
          : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/30'
        }
        ${isPlaying ? 'ring-2 ring-cyan-400' : ''}
      `}
      style={{ left: displayLeft, width: displayWidth }}
      onClick={onSelect}
      title={`${program.title}\n${formatTime(program.start, program.start_timestamp)} - ${formatTime(program.end, program.stop_timestamp)}`}
    >
      <div className="flex items-start gap-1 h-full">
        {isLive && (
          <span className="flex-shrink-0 px-1 py-0.5 bg-red-600 text-white text-[9px] font-bold rounded mt-0.5">
            LIVE
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-medium truncate ${isLive ? 'text-white' : 'text-gray-200'}`}>
            {program.title || 'No Title'}
          </p>
          <p className="text-[10px] text-gray-500 truncate">
            {formatTime(program.start, program.start_timestamp)} - {formatTime(program.end, program.stop_timestamp)}
          </p>
        </div>
      </div>
      
      {/* Hover play button */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
          <Play size={16} className="text-black ml-0.5" fill="black" />
        </div>
      </div>
    </div>
  );
};

// Channel row component
const ChannelRow = ({ 
  channel, 
  programs, 
  timelineStart, 
  timelineEnd, 
  hoursToShow, 
  onPlayChannel, 
  isFavorite, 
  onToggleFavorite,
  isDraggable,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver,
  dragIndex,
  isMobile
}) => {
  const slotWidth = isMobile ? SLOT_WIDTH_MOBILE : SLOT_WIDTH;
  const hourWidth = isMobile ? HOUR_WIDTH_MOBILE : HOUR_WIDTH;
  const rowHeight = isMobile ? CHANNEL_ROW_HEIGHT_MOBILE : CHANNEL_ROW_HEIGHT;
  const sidebarWidth = isMobile ? CHANNEL_SIDEBAR_WIDTH_MOBILE : CHANNEL_SIDEBAR_WIDTH;
  
  // Generate filled time slots - use channel name for gaps instead of "No data"
  const generateFilledSlots = () => {
    if (programs.length === 0) {
      // No programs at all - create one big slot covering entire timeline
      return [{
        id: 'channel-slot',
        title: channel.name,
        start: timelineStart,
        end: timelineEnd,
        isChannelFiller: true
      }];
    }
    
    // Sort programs by start time and get their effective ranges (clipped to timeline)
    const sortedPrograms = [...programs]
      .map(p => ({
        start: Math.max(parseDate(p.start, p.start_timestamp).getTime(), timelineStart),
        end: Math.min(parseDate(p.end, p.stop_timestamp).getTime(), timelineEnd)
      }))
      .filter(r => r.end > r.start && r.start < timelineEnd)
      .sort((a, b) => a.start - b.start);
    
    if (sortedPrograms.length === 0) {
      // All programs are outside the timeline
      return [{
        id: 'channel-slot',
        title: channel.name,
        start: timelineStart,
        end: timelineEnd,
        isChannelFiller: true
      }];
    }
    
    const fillers = [];
    let currentPosition = timelineStart;
    
    // Find gaps between programs
    for (const prog of sortedPrograms) {
      if (prog.start > currentPosition) {
        // There's a gap before this program
        fillers.push({
          id: `filler-${currentPosition}`,
          title: channel.name,
          start: currentPosition,
          end: prog.start,
          isChannelFiller: true
        });
      }
      // Move position to end of this program (or keep it if this program ends earlier)
      currentPosition = Math.max(currentPosition, prog.end);
    }
    
    // Check if there's a gap after the last program
    if (currentPosition < timelineEnd) {
      fillers.push({
        id: `filler-${currentPosition}`,
        title: channel.name,
        start: currentPosition,
        end: timelineEnd,
        isChannelFiller: true
      });
    }
    
    return fillers;
  };
  
  const fillerSlots = generateFilledSlots();
  
  // Calculate program width for mobile
  const calcWidth = (program) => {
    const programStart = parseDate(program.start, program.start_timestamp).getTime();
    const programEnd = parseDate(program.end, program.stop_timestamp).getTime();
    const effectiveStart = Math.max(programStart, timelineStart);
    const effectiveEnd = timelineEnd ? Math.min(programEnd, timelineEnd) : programEnd;
    const durationMinutes = (effectiveEnd - effectiveStart) / (1000 * 60);
    return Math.max(0, (durationMinutes / 30) * slotWidth);
  };
  
  const calcLeft = (program) => {
    const startTime = parseDate(program.start, program.start_timestamp).getTime();
    if (startTime <= timelineStart) return 0;
    const offsetMinutes = (startTime - timelineStart) / (1000 * 60);
    return (offsetMinutes / 30) * slotWidth;
  };
  
  return (
    <div 
      className={`flex border-b border-white/10 hover:bg-white/5 transition-colors ${
        isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
      } ${isDragOver ? 'bg-cyan-500/20 border-cyan-500/50' : ''}`}
      style={{ height: rowHeight }}
      draggable={isDraggable}
      onDragStart={(e) => isDraggable && onDragStart?.(e, dragIndex)}
      onDragOver={(e) => isDraggable && onDragOver?.(e, dragIndex)}
      onDrop={(e) => isDraggable && onDrop?.(e, dragIndex)}
    >
      {/* Channel info sidebar - responsive width */}
      <div 
        className="flex-shrink-0 flex items-center gap-2 sm:gap-3 px-2 sm:px-3 bg-black/50 border-r border-white/10 sticky left-0 z-10"
        style={{ width: sidebarWidth }}
      >
        {/* Drag handle for favorites - hidden on mobile */}
        {isDraggable && !isMobile && (
          <div className="flex-shrink-0 text-gray-500 hover:text-gray-300 cursor-grab">
            <GripVertical size={16} />
          </div>
        )}
        {/* Channel logo - smaller on mobile */}
        <div className={`${isMobile ? 'w-9 h-9' : 'w-12 h-12'} rounded-lg bg-gray-800 overflow-hidden flex items-center justify-center flex-shrink-0`}>
          {channel.logo ? (
            <img 
              src={`/api/iptv/image-proxy?url=${encodeURIComponent(channel.logo)}`}
              alt={channel.name}
              className="w-full h-full object-contain p-1"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div className={`${channel.logo ? 'hidden' : ''} w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-900/50 to-gray-900`}>
            <span className={`text-cyan-400 font-bold ${isMobile ? 'text-sm' : 'text-lg'}`}>{channel.name?.charAt(0) || 'TV'}</span>
          </div>
        </div>
        
        {/* Channel name and favorite */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <p className={`text-white font-medium truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>{channel.name}</p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(channel.id);
              }}
              className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
            >
              <Heart 
                size={isMobile ? 12 : 14} 
                className={isFavorite ? "text-red-500" : "text-gray-500 hover:text-red-400"} 
                fill={isFavorite ? "currentColor" : "none"}
              />
            </button>
          </div>
          {!isMobile && <p className="text-gray-500 text-xs truncate">{channel.category}</p>}
        </div>
      </div>
      
      {/* Programs timeline - Fixed width for timeline */}
      <div className="relative flex-shrink-0 overflow-hidden" style={{ width: hourWidth * hoursToShow }}>
        {/* Render actual programs */}
        {programs.map((program, idx) => {
          const endTime = parseDate(program.end, program.stop_timestamp).getTime();
          if (endTime <= timelineStart) return null;
          const startTime = parseDate(program.start, program.start_timestamp).getTime();
          if (timelineEnd && startTime >= timelineEnd) return null;
          
          const width = calcWidth(program);
          const left = calcLeft(program);
          const isLive = isCurrentlyAiring(program);
          
          if (width <= 0) return null;
          
          const cardMargin = 2;
          const displayLeft = left + cardMargin;
          const displayWidth = Math.max(width - (cardMargin * 2), 36);
          
          return (
            <div
              key={program.id || idx}
              className={`absolute top-1 bottom-1 rounded-lg px-1 sm:px-2 py-1 overflow-hidden cursor-pointer transition-all duration-200 group
                ${isLive 
                  ? 'bg-cyan-600/30 border border-cyan-500/50 hover:bg-cyan-600/50 hover:border-cyan-400' 
                  : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/30'
                }`}
              style={{ left: displayLeft, width: displayWidth }}
              onClick={() => onPlayChannel(channel)}
              title={`${program.title}\n${formatTime(program.start, program.start_timestamp)} - ${formatTime(program.end, program.stop_timestamp)}`}
            >
              <div className="flex items-start gap-1 h-full">
                {isLive && (
                  <span className={`flex-shrink-0 px-1 py-0.5 bg-red-600 text-white font-bold rounded mt-0.5 ${isMobile ? 'text-[7px]' : 'text-[9px]'}`}>
                    LIVE
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className={`font-medium truncate ${isLive ? 'text-white' : 'text-gray-200'} ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                    {program.title || 'No Title'}
                  </p>
                  {!isMobile && (
                    <p className="text-[10px] text-gray-500 truncate">
                      {formatTime(program.start, program.start_timestamp)} - {formatTime(program.end, program.stop_timestamp)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Hover play button */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} rounded-full bg-white/90 flex items-center justify-center`}>
                  <Play size={isMobile ? 12 : 16} className="text-black ml-0.5" fill="black" />
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Render filler slots for gaps - shows channel name */}
        {fillerSlots.map((slot) => {
          const left = ((slot.start - timelineStart) / (30 * 60 * 1000)) * slotWidth;
          const width = ((slot.end - slot.start) / (30 * 60 * 1000)) * slotWidth;
          
          const cardMargin = 2;
          const displayLeft = left + cardMargin;
          const displayWidth = Math.max(width - (cardMargin * 2), 36);
          
          return (
            <div
              key={slot.id}
              className="absolute top-1 bottom-1 rounded-lg bg-gradient-to-r from-gray-800/50 to-gray-900/50 border border-white/10 flex items-center px-1 sm:px-2 cursor-pointer hover:bg-gray-700/50 hover:border-white/20 transition-colors overflow-hidden"
              style={{ left: displayLeft, width: displayWidth }}
              onClick={() => onPlayChannel(channel)}
              title={`Watch ${channel.name}`}
            >
              <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                <Tv size={isMobile ? 12 : 14} className="text-gray-500 flex-shrink-0" />
                <span className={`text-gray-400 font-medium truncate ${isMobile ? 'text-[10px]' : 'text-xs'}`}>{slot.title}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main TV Guide component
const TVGuide = ({ onPlayChannel, favorites, onToggleFavorite, showFavoritesOnly = false, onReorderFavorites }) => {
  const [channels, setChannels] = useState([]);
  const [programs, setPrograms] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [timelineStart, setTimelineStart] = useState(() => {
    // Start timeline at the beginning of the current hour
    const now = new Date();
    // Round DOWN to the current hour (not half-hour)
    now.setMinutes(0, 0, 0);
    return now.getTime();
  });
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Drag and drop state for reordering favorites
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  const scrollContainerRef = useRef(null);
  const hoursToShow = 4; // Show 4 hours ahead
  
  // Drag handlers for reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    
    // Since channels are sorted to match favorites order,
    // we can directly use the channel IDs to build the new order
    const newChannelsOrder = [...channels];
    const [draggedChannel] = newChannelsOrder.splice(draggedIndex, 1);
    newChannelsOrder.splice(dropIndex, 0, draggedChannel);
    
    // Extract the channel IDs in the new order
    const newFavorites = newChannelsOrder.map(c => c.id);
    
    console.log('Reordering favorites:', { 
      from: draggedIndex, 
      to: dropIndex,
      newOrder: newFavorites 
    });
    
    // Call the parent's reorder handler
    if (onReorderFavorites) {
      onReorderFavorites(newFavorites);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  
  // Calculate timeline end (4 hours after start)
  const timelineEnd = timelineStart + (hoursToShow * 60 * 60 * 1000);
  
  // Fetch EPG data
  const fetchEPGData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      if (showFavoritesOnly) {
        // Fetch EPG data for favorite channels only
        if (favorites.length === 0) {
          setChannels([]);
          setPrograms({});
          setLoading(false);
          return;
        }
        
        // First, get the channel details for favorites
        const channelsData = await apiCall(`/iptv/channels/by-ids?ids=${favorites.join(',')}`);
        
        if (channelsData.status !== 'success' || !channelsData.channels?.length) {
          setError('Could not load favorite channels');
          setLoading(false);
          return;
        }
        
        // Then fetch EPG for these channels
        const epgData = await apiCall(`/iptv/epg?stream_ids=${favorites.join(',')}&limit=20`);
        
        // Sort channels to match the order of favorites array
        const sortedChannels = [...channelsData.channels].sort((a, b) => {
          const indexA = favorites.indexOf(a.id);
          const indexB = favorites.indexOf(b.id);
          return indexA - indexB;
        });
        
        if (epgData.status === 'success') {
          setChannels(sortedChannels);
          setPrograms(epgData.epg || {});
        } else {
          // Still show channels even without EPG data
          setChannels(sortedChannels);
          setPrograms({});
        }
      } else {
        // Fetch bulk EPG data for all channels with EPG support
        const data = await apiCall('/iptv/epg/bulk?hours=4');
        
        if (data.status === 'success') {
          setChannels(data.channels || []);
          setPrograms(data.programs || {});
        } else {
          setError(data.message || 'Failed to load EPG data');
        }
      }
    } catch (err) {
      console.error('EPG fetch error:', err);
      setError('Failed to connect to EPG service');
    } finally {
      setLoading(false);
    }
  }, [showFavoritesOnly, favorites]);
  
  useEffect(() => {
    fetchEPGData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchEPGData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEPGData]);
  
  // Re-sort channels when favorites order changes (for drag and drop reordering)
  useEffect(() => {
    if (showFavoritesOnly && channels.length > 0) {
      const sortedChannels = [...channels].sort((a, b) => {
        const indexA = favorites.indexOf(a.id);
        const indexB = favorites.indexOf(b.id);
        return indexA - indexB;
      });
      
      // Only update if order actually changed
      const currentOrder = channels.map(c => c.id).join(',');
      const newOrder = sortedChannels.map(c => c.id).join(',');
      if (currentOrder !== newOrder) {
        setChannels(sortedChannels);
      }
    }
  }, [favorites, showFavoritesOnly]);
  
  // Update now indicator every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // No horizontal scrolling needed - timeline starts at current time
  // and programs are clipped to fit within the 4-hour window
  
  // Navigate time
  const navigateTime = (direction) => {
    const offsetMs = direction === 'forward' ? 2 * 60 * 60 * 1000 : -2 * 60 * 60 * 1000;
    setTimelineStart(prev => prev + offsetMs);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-cyan-400" size={40} />
          <p className="text-gray-400">Loading {showFavoritesOnly ? 'Favorites' : 'TV'} Guide...</p>
          <p className="text-gray-500 text-sm mt-2">
            {showFavoritesOnly 
              ? `Fetching program data for ${favorites.length} favorite channel${favorites.length !== 1 ? 's' : ''}`
              : `Fetching program data for ${hoursToShow} hours`}
          </p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 text-center">
        <Tv className="text-red-400 mx-auto mb-3" size={40} />
        <h3 className="text-red-400 font-medium mb-2">Failed to Load TV Guide</h3>
        <p className="text-red-400/70 text-sm mb-4">{error}</p>
        <button
          onClick={fetchEPGData}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (channels.length === 0) {
    return (
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-6 text-center">
        <Info className="text-yellow-400 mx-auto mb-3" size={40} />
        <h3 className="text-yellow-400 font-medium mb-2">
          {showFavoritesOnly ? 'No Favorite Channels' : 'No EPG Data Available'}
        </h3>
        <p className="text-yellow-400/70 text-sm">
          {showFavoritesOnly 
            ? 'Add channels to your favorites to see them in the TV Guide. Click the heart icon on any channel in Grid view.'
            : 'Your IPTV provider does not have program guide data for the available channels.'}
        </p>
      </div>
    );
  }
  
  // Calculate responsive dimensions
  const sidebarWidth = isMobile ? CHANNEL_SIDEBAR_WIDTH_MOBILE : CHANNEL_SIDEBAR_WIDTH;
  const hourWidth = isMobile ? HOUR_WIDTH_MOBILE : HOUR_WIDTH;
  
  return (
    <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="tv-guide">
      {/* Header - Responsive layout */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border-b border-white/10 bg-black/50 gap-3 sm:gap-0">
        <div className="flex items-center gap-2 sm:gap-3">
          {showFavoritesOnly ? (
            <Heart className="text-red-500" size={isMobile ? 20 : 24} fill="currentColor" />
          ) : (
            <Tv className="text-cyan-400" size={isMobile ? 20 : 24} />
          )}
          <div>
            <h2 className={`text-white font-bold ${isMobile ? 'text-base' : 'text-lg'}`}>
              {showFavoritesOnly ? 'Favorites Guide' : 'TV Guide'}
            </h2>
            <p className="text-gray-400 text-xs sm:text-sm">
              {channels.length} {showFavoritesOnly ? 'favorite' : ''} channel{channels.length !== 1 ? 's' : ''} with program data
            </p>
          </div>
        </div>
        
        {/* Time navigation - Centered on mobile */}
        <div className="flex items-center justify-center sm:justify-end gap-1 sm:gap-2">
          <button
            onClick={() => navigateTime('back')}
            className="p-1.5 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            title="Go back 2 hours"
          >
            <ChevronLeft size={isMobile ? 18 : 20} className="text-white" />
          </button>
          
          <div className="px-2 sm:px-4 py-1.5 sm:py-2 bg-white/5 rounded-lg border border-white/10">
            <span className={`text-white font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
              {new Date(timelineStart).toLocaleDateString('en-US', { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric',
                timeZone: 'America/Chicago'
              })}
              {!isMobile && <span className="text-gray-400"> (CST)</span>}
            </span>
          </div>
          
          <button
            onClick={() => navigateTime('forward')}
            className="p-1.5 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            title="Go forward 2 hours"
          >
            <ChevronRight size={isMobile ? 18 : 20} className="text-white" />
          </button>
          
          {/* Refresh button */}
          <button
            onClick={fetchEPGData}
            className="p-1.5 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors ml-1 sm:ml-2"
            title="Refresh EPG data"
          >
            <RefreshCw size={isMobile ? 18 : 20} className="text-white" />
          </button>
        </div>
      </div>
      
      {/* Guide grid - Responsive with horizontal scroll on mobile */}
      <div 
        ref={scrollContainerRef}
        className="overflow-y-auto overflow-x-auto relative"
        style={{ maxHeight: isMobile ? 'calc(100vh - 450px)' : 'calc(100vh - 400px)', minHeight: isMobile ? 300 : 400 }}
      >
        {/* Container for timeline */}
        <div className="min-w-max" style={{ width: sidebarWidth + (hourWidth * hoursToShow) }}>
          {/* Time header */}
          <TimeHeader timelineStart={timelineStart} hoursToShow={hoursToShow} isMobile={isMobile} />
          
          {/* Channel rows with Now indicator spanning full height */}
          <div className="relative">
            {/* Now indicator - spans all channel rows */}
            <NowIndicator timelineStart={timelineStart} currentTime={currentTime} isMobile={isMobile} />
            
            {channels.map((channel, index) => (
              <ChannelRow
                key={channel.id}
                channel={channel}
                programs={programs[String(channel.id)] || []}
                timelineStart={timelineStart}
                timelineEnd={timelineEnd}
                hoursToShow={hoursToShow}
                onPlayChannel={onPlayChannel}
                isFavorite={favorites.includes(channel.id)}
                onToggleFavorite={onToggleFavorite}
                isDraggable={showFavoritesOnly && !isMobile}
                dragIndex={index}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isDragOver={dragOverIndex === index}
                isMobile={isMobile}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Footer info - Hide on mobile or show compact version */}
      <div className="p-2 sm:p-3 border-t border-white/10 bg-black/50 flex items-center justify-between">
        <p className="text-gray-500 text-[10px] sm:text-xs">
          <Clock size={isMobile ? 10 : 12} className="inline mr-1" />
          Updated: {new Date().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true,
            timeZone: 'America/Chicago'
          })} CST
        </p>
        {!isMobile && (
          <p className="text-gray-500 text-xs">
            Click any program to watch
          </p>
        )}
      </div>
    </div>
  );
};

export default TVGuide;
